/**
 * Canvas-drawn stickers + image-based stickers + image-based frames for
 * the share-tip PNG.
 *
 * - The 6 original stickers (5 Stars, Must Visit, Anniversary, Birthday,
 *   Spicy, Best Ever) are pure-Canvas drawing routines — zero assets.
 * - The 24 v2 stickers (seasonal/holiday/celebration) are image-based;
 *   transparent PNGs live in /public/assets/stickers-v2/.
 * - The 12 frames are image-based overlays wrapped AROUND the photo;
 *   transparent PNGs live in /public/assets/frames/.
 *
 * If an image asset is missing (404), the renderer skips silently so the
 * share image still ships. This means the picker UI works immediately
 * even before the user uploads the PNGs.
 *
 * To add a new canvas-drawn sticker:
 *   1) add an entry to STICKERS_CLASSIC with id, label, emoji
 *   2) add a `draw(ctx, x, y)` rendering routine in STICKER_DRAWERS
 */

const STICKERS_CLASSIC = [
  { id: "five_star", label: "5 Stars", emoji: "⭐" },
  { id: "must_visit", label: "Must Visit", emoji: "✓" },
  { id: "anniversary", label: "Anniversary", emoji: "💞" },
  { id: "birthday", label: "Birthday", emoji: "🎂" },
  { id: "spicy", label: "Spicy", emoji: "🌶" },
  { id: "best_ever", label: "Best Ever", emoji: "🏆" },
];

// Image-based v2 stickers. `src` resolves to a transparent PNG in
// /public/assets/stickers-v2/. Missing files fall back gracefully.
const STICKERS_V2 = [
  { id: "happy_new_year", label: "Happy New Year", emoji: "🎊" },
  { id: "winter_fun", label: "Winter Fun", emoji: "❄️" },
  { id: "hello_spring", label: "Hello Spring", emoji: "🌷" },
  { id: "summer_vibes", label: "Summer Vibes", emoji: "☀️" },
  { id: "autumn_days", label: "Autumn Days", emoji: "🍁" },
  { id: "lets_party", label: "Let's Party", emoji: "🎉" },
  { id: "congrats", label: "Congrats", emoji: "🎓" },
  { id: "welcome", label: "Welcome", emoji: "👋" },
  { id: "thank_you", label: "Thank You", emoji: "💌" },
  { id: "cheers", label: "Cheers", emoji: "🥂" },
  { id: "good_vibes", label: "Good Vibes", emoji: "✨" },
  { id: "celebrate", label: "Celebrate", emoji: "🎇" },
  { id: "happy_birthday", label: "Happy Birthday", emoji: "🎂" },
  { id: "valentines", label: "Valentine's", emoji: "💕" },
  { id: "lucky_day", label: "Lucky Day", emoji: "🍀" },
  { id: "happy_easter", label: "Happy Easter", emoji: "🐣" },
  { id: "mothers_day", label: "Mother's Day", emoji: "🌹" },
  { id: "fathers_day", label: "Father's Day", emoji: "👔" },
  { id: "july_4th", label: "4th of July", emoji: "🎆" },
  { id: "happy_halloween", label: "Halloween", emoji: "👻" },
  { id: "thanksgiving", label: "Thanksgiving", emoji: "🦃" },
  { id: "merry_christmas", label: "Merry Christmas", emoji: "🎄" },
  { id: "baby_shower", label: "Baby Shower", emoji: "🍼" },
  { id: "love_xoxo", label: "Love XOXO", emoji: "💋" },
].map((s) => ({ ...s, src: `/assets/stickers-v2/${s.id}.png`, kind: "image" }));

export const STICKERS = [
  ...STICKERS_CLASSIC.map((s) => ({ ...s, kind: "classic" })),
  ...STICKERS_V2,
];

// Image-based frame overlays. Each frame is a transparent PNG that
// wraps around the photo box on the postcard. All 15 watercolor frames
// share a similar decorative-border layout (transparent interior,
// painted corners), so we use a single conservative default inner
// bound. The user fine-tunes the fit via 2-finger pinch resize in the
// Position Frame view — `inner` is just the picker preview hint and
// the canvas starting scale.
const DEFAULT_FRAME_INNER = { x: 0.08, y: 0.12, w: 0.84, h: 0.76 };
const FRAME_INNER = {
  thanksgiving: { x: 0.247, y: 0.197, w: 0.571, h: 0.555 },
  christmas_1:  { x: 0.208, y: 0.18,  w: 0.613, h: 0.577 },
};

export const FRAMES = [
  // Seasonal / holiday
  { id: "mothers_day",    label: "Mother's Day",  emoji: "🌹" },
  { id: "fathers_day",    label: "Father's Day",  emoji: "👔" },
  { id: "easter",         label: "Easter",        emoji: "🐰" },
  { id: "valentines_00",  label: "Valentine's",   emoji: "💘" },
  { id: "valentines_1",   label: "Valentine's 2", emoji: "💕" },
  { id: "july_4th_00",    label: "4th of July",   emoji: "🎆" },
  { id: "halloween_00",   label: "Halloween",     emoji: "🎃" },
  { id: "thanksgiving",   label: "Thanksgiving",  emoji: "🦃" },
  { id: "christmas_1",    label: "Christmas",     emoji: "🎄" },
  { id: "new_year",       label: "New Year",      emoji: "🎊" },
  // Life / leisure
  { id: "beach",          label: "Beach",         emoji: "🏖" },
  { id: "birthday_party", label: "Birthday",      emoji: "🎂" },
  { id: "baby_bear",      label: "Baby Bear",     emoji: "🧸" },
  { id: "baby_blue_00",   label: "Baby Blue",     emoji: "👶" },
  { id: "baby_01",        label: "Baby",          emoji: "🐘" },
].map((f) => ({
  ...f,
  src: `/assets/frames/${f.id}.png`,
  inner: FRAME_INNER[f.id] || DEFAULT_FRAME_INNER,
}));

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function star(ctx, cx, cy, outer, inner, points = 5) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / points - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function heart(ctx, cx, cy, size) {
  const s = size / 2;
  ctx.beginPath();
  ctx.moveTo(cx, cy + s * 0.5);
  ctx.bezierCurveTo(cx, cy + s * 0.2, cx - s, cy - s * 0.2, cx - s, cy - s * 0.5);
  ctx.bezierCurveTo(cx - s, cy - s, cx - s * 0.4, cy - s, cx, cy - s * 0.4);
  ctx.bezierCurveTo(cx + s * 0.4, cy - s, cx + s, cy - s, cx + s, cy - s * 0.5);
  ctx.bezierCurveTo(cx + s, cy - s * 0.2, cx, cy + s * 0.2, cx, cy + s * 0.5);
  ctx.closePath();
}

const SHADOW = (ctx) => {
  ctx.shadowColor = "rgba(14,44,84,0.25)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 4;
};

/* ---------- Renderers ---------- */

const STICKER_DRAWERS = {
  /** Gold 5-star ribbon, rotated slightly. */
  five_star: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.12);
    SHADOW(ctx);
    // Pill
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = "#0E2C54";
    ctx.lineWidth = 4;
    roundRectPath(ctx, -160, -55, 320, 110, 55);
    ctx.fill();
    ctx.stroke();
    ctx.shadowColor = "transparent";
    // 5 gold stars
    const STAR_Y = -3;
    const STAR_R = 16;
    for (let i = 0; i < 5; i++) {
      const sx = -120 + i * 60;
      star(ctx, sx, STAR_Y, STAR_R, STAR_R * 0.45, 5);
      ctx.fillStyle = "#F4C430";
      ctx.fill();
      ctx.strokeStyle = "#A6800F";
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }
    ctx.restore();
  },

  /** Green "MUST VISIT" stamp with checkmark badge. */
  must_visit: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.18);
    SHADOW(ctx);
    ctx.strokeStyle = "#2E7D5A";
    ctx.fillStyle = "#FFFFFF";
    ctx.lineWidth = 6;
    roundRectPath(ctx, -180, -55, 360, 110, 16);
    ctx.fill();
    ctx.stroke();
    // Inner border ring
    ctx.lineWidth = 2;
    roundRectPath(ctx, -170, -45, 340, 90, 10);
    ctx.stroke();
    ctx.shadowColor = "transparent";
    // Text
    ctx.font = "900 44px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillStyle = "#2E7D5A";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("MUST VISIT", 18, 4);
    // Checkmark badge on the left
    ctx.beginPath();
    ctx.arc(-130, 0, 26, 0, Math.PI * 2);
    ctx.fillStyle = "#2E7D5A";
    ctx.fill();
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(-142, 2);
    ctx.lineTo(-132, 12);
    ctx.lineTo(-118, -8);
    ctx.stroke();
    ctx.restore();
  },

  /** Orange "ANNIVERSARY" heart stamp. */
  anniversary: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.1);
    SHADOW(ctx);
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = "#E55B2C";
    ctx.lineWidth = 5;
    roundRectPath(ctx, -180, -60, 360, 120, 60);
    ctx.fill();
    ctx.stroke();
    ctx.shadowColor = "transparent";
    // Heart
    heart(ctx, -130, 5, 60);
    ctx.fillStyle = "#E55B2C";
    ctx.fill();
    ctx.strokeStyle = "#0E2C54";
    ctx.lineWidth = 2;
    ctx.stroke();
    // Text
    ctx.font = "900 38px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillStyle = "#0E2C54";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("ANNIVERSARY", 30, 4);
    ctx.restore();
  },

  /** Pink "BIRTHDAY 🎂" sticker. */
  birthday: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(0.1);
    SHADOW(ctx);
    ctx.fillStyle = "#FCE4EC";
    ctx.strokeStyle = "#D81B60";
    ctx.lineWidth = 5;
    roundRectPath(ctx, -170, -55, 340, 110, 55);
    ctx.fill();
    ctx.stroke();
    ctx.shadowColor = "transparent";
    // Birthday cake glyph (simple rectangle + candle)
    ctx.fillStyle = "#D81B60";
    roundRectPath(ctx, -140, -10, 50, 35, 6);
    ctx.fill();
    ctx.fillStyle = "#F4C430";
    ctx.fillRect(-118, -25, 6, 15); // candle
    ctx.beginPath();
    ctx.arc(-115, -28, 5, 0, Math.PI * 2);
    ctx.fillStyle = "#E55B2C";
    ctx.fill();
    // Text
    ctx.font = "900 42px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillStyle = "#D81B60";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("BIRTHDAY", 25, 5);
    ctx.restore();
  },

  /** Red chili "SPICY" tag. */
  spicy: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.14);
    SHADOW(ctx);
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = "#C81E1E";
    ctx.lineWidth = 5;
    roundRectPath(ctx, -150, -55, 300, 110, 55);
    ctx.fill();
    ctx.stroke();
    ctx.shadowColor = "transparent";
    // Chili (curved teardrop)
    ctx.fillStyle = "#C81E1E";
    ctx.beginPath();
    ctx.moveTo(-110, -25);
    ctx.quadraticCurveTo(-130, 0, -100, 25);
    ctx.quadraticCurveTo(-70, 30, -65, 5);
    ctx.quadraticCurveTo(-70, -15, -110, -25);
    ctx.closePath();
    ctx.fill();
    // Stem
    ctx.fillStyle = "#2E7D5A";
    ctx.fillRect(-115, -32, 18, 10);
    // Text
    ctx.font = "900 40px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillStyle = "#C81E1E";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("SPICY", 35, 4);
    ctx.restore();
  },

  /** Gold "BEST EVER" trophy. */
  best_ever: (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(0.08);
    SHADOW(ctx);
    ctx.fillStyle = "#0E2C54";
    roundRectPath(ctx, -185, -55, 370, 110, 16);
    ctx.fill();
    ctx.shadowColor = "transparent";
    // Gold inner border
    ctx.strokeStyle = "#F4C430";
    ctx.lineWidth = 3;
    roundRectPath(ctx, -175, -45, 350, 90, 10);
    ctx.stroke();
    // Trophy cup
    ctx.fillStyle = "#F4C430";
    ctx.beginPath();
    ctx.moveTo(-140, -25);
    ctx.lineTo(-100, -25);
    ctx.lineTo(-105, 15);
    ctx.lineTo(-135, 15);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(-130, 15, 20, 8);
    ctx.fillRect(-135, 23, 30, 6);
    // Text
    ctx.font = "900 40px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillStyle = "#F4C430";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("BEST EVER", 40, 4);
    ctx.restore();
  },
};

/**
 * Draw a sticker by id at canvas coordinates (x, y) which represents the
 * sticker's CENTER. Classic stickers are canvas-drawn synchronously;
 * v2 stickers load a transparent PNG (async).
 *
 * `scale` (default 1) multiplies the sticker's natural draw size — used
 * when the user pinches to resize in the Position Sticker view.
 *
 * Returns a Promise so the caller can await image-based stickers; classic
 * resolutions return immediately.
 */
export function drawSticker(ctx, stickerId, x, y, scale = 1) {
  // Classic canvas-drawn sticker → synchronous.
  const fn = STICKER_DRAWERS[stickerId];
  if (fn) {
    // Use ctx transform so the existing drawing routines scale uniformly
    // around the (x, y) anchor point.
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    fn(ctx, 0, 0);
    ctx.restore();
    return Promise.resolve();
  }
  // Image-based v2 sticker.
  const meta = STICKERS_V2.find((s) => s.id === stickerId);
  if (!meta) return Promise.resolve();
  return loadImageSafe(meta.src).then((img) => {
    if (!img) return; // missing asset — silent fallback
    const STICKER_W = 280 * scale;
    const aspect = img.width / img.height || 1;
    const w = STICKER_W;
    const h = Math.round(w / aspect);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.08); // subtle scrapbook tilt
    SHADOW(ctx);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  });
}

/**
 * Draw a frame overlay. `bounds` = { x, y, w, h } describes the FRAME's
 * outer rectangle on the canvas (the photo is already drawn smaller,
 * fitting inside the frame's transparent inner window — see
 * `shareTipCard.js`). We simply stretch the frame PNG to fill `bounds`.
 *
 * If the PNG asset is missing the frame is skipped silently.
 */
export function drawFrame(ctx, frameId, bounds) {
  const meta = FRAMES.find((f) => f.id === frameId);
  if (!meta) return Promise.resolve();
  return loadImageSafe(meta.src).then((img) => {
    if (!img) return;
    ctx.save();
    ctx.drawImage(img, bounds.x, bounds.y, bounds.w, bounds.h);
    ctx.restore();
  });
}

// Cached image loader that swallows 404s and returns null instead of
// rejecting — keeps the picker usable before user uploads the PNGs.
const _imageCache = new Map();
function loadImageSafe(src) {
  if (_imageCache.has(src)) return _imageCache.get(src);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
  _imageCache.set(src, p);
  return p;
}
